import { createSlice, Dictionary, PayloadAction } from '@reduxjs/toolkit'
import * as GuardrailsOpenAPI from '@src/endpoints/openapis/saas-cam-guardrails'
import type { State } from '@src/redux/configureStore'
import { AppStorage } from '@src/utils/storage.helpers'
import { storagePrefix } from '@src/constants/constants'
import { correctEmailFormat } from '@src/utils/accounts'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import { toastSuccess } from '@src/utils/toast'
import {
  getNormalizedGuardrails,
  getNormalizedGuardrailsFolder
} from './helpers'
import {
  GuardrailsFolders,
  GuardrailModel,
  GuardrailsDict,
  GuardrailSummaryModel
} from './models'
import { Folder } from '@src/endpoints/openapis/saas-cam-guardrails'
import { forEach } from 'lodash'
import { orderByField } from '@src/utils/array.utils'

const storage = new AppStorage(storagePrefix, sessionStorage)

export const initialState = {
  guardrailsFolders: {} as GuardrailsFolders,
  guardrails: {} as GuardrailsDict,
  guardrailsList: [] as GuardrailSummaryModel[],
  showOnlyEnabledGuardrails: false,
  guardrailsCount: {
    total: 0,
    active: 0
  },
  guardrailInfo: null as GuardrailModel,
  isPending: false,
  error: null as any
}

const guardrails = createSlice({
  name: 'guardrails',
  initialState,
  reducers: {
    changeState: (
      state,
      action: PayloadAction<Partial<typeof initialState>>
    ) => ({ ...state, ...action.payload }),
    pending: (state, action: PayloadAction<boolean>) => {
      state.isPending = action.payload
    },
    cleanerStat: (state, action: PayloadAction<keyof typeof initialState>) => {
      state[action.payload] = initialState[action.payload]
    },
    errors: (state, action: PayloadAction<any>) => {
      state.error = action.payload
      state.isPending = false
    },
    setFlagOnlyEnabledGuardrails: (state, action: PayloadAction<boolean>) => {
      state.showOnlyEnabledGuardrails = action.payload
    },
    createdGuardrail: (state, action: PayloadAction<GuardrailModel>) => {
      state.isPending = false
    },
    updatedGuardrail: (state, action: PayloadAction<GuardrailModel>) => {
      state.isPending = false
    },
    loadedGuardrail: (state, action: PayloadAction<GuardrailModel>) => {
      state.guardrailInfo = action.payload
      state.isPending = false
    },
    loadedGuardrailsFolders: (
      state,
      action: PayloadAction<GuardrailsFolders>
    ) => {
      state.guardrailsFolders = action.payload
      state.isPending = false
    },

    loadedGuardrails: (
      state,
      action: PayloadAction<{
        normalizedList: GuardrailsDict
        list: GuardrailSummaryModel[]
      }>
    ) => {
      state.guardrails = action.payload?.normalizedList
      state.guardrailsList = action.payload?.list
      state.isPending = false
    },

    dispose: state => initialState
  }
})

export function getGuardrailsCategories(
  callbackSuccess?: (
    normalizedList: Dictionary<GuardrailsOpenAPI.Folder>
  ) => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.cleanerStat('guardrailsFolders'))
    dispatch(guardrails.actions.pending(true))
    GuardrailsOpenAPI.getFolders()
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        const folderList = orderByField({
          options: res,
          key: 'name',
          orders: 'asc'
        })
        const normalizedList = getNormalizedGuardrailsFolder(folderList)
        dispatch(guardrails.actions.loadedGuardrailsFolders(normalizedList))
        if (callbackSuccess) {
          callbackSuccess(normalizedList)
        }
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export function getGuardrailsList(callbackSuccess?: () => void) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.cleanerStat('guardrailInfo'))
    dispatch(guardrails.actions.pending(true))
    GuardrailsOpenAPI.getGuardrails({})
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        const list = res || []
        const normalizedList = getNormalizedGuardrails(list)
        dispatch(guardrails.actions.loadedGuardrails({ normalizedList, list }))
        if (callbackSuccess) {
          callbackSuccess()
        }
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export function getGuardrail(
  id: string,
  callback?: (
    data: GuardrailsOpenAPI.APIResponse<GuardrailsOpenAPI.Guardrail>
  ) => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    dispatch(guardrails.actions.cleanerStat('guardrailInfo'))
    GuardrailsOpenAPI.getGuardrail({ id })
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        dispatch(guardrails.actions.loadedGuardrail(res))
        if (callback) callback(res)
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export function patchGuardrails(
  patchItems: GuardrailSummaryModel[],
  callback?: () => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    const requestsPromise = []
    forEach(patchItems, item => {
      requestsPromise.push(
        GuardrailsOpenAPI.updateGuardrail({ id: item?.id, guardrail: item })
          .then(res =>
            defaultHandleStatus(
              res,
              () => {
                dispatch(guardrails.actions.pending(false))
              },
              true
            )
          )
          .catch((res: Response) => dispatch(guardrails.actions.pending(false)))
      )
    })

    Promise.all([requestsPromise]).then(res => {
      dispatch(guardrails.actions.pending(false))
      dispatch(getGuardrailsList())
      if (callback) callback()
    })
  }
}

export function deleteGuardrail(id: string, callBack?: () => void) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    return GuardrailsOpenAPI.deleteGuardrail({ id })
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        if (typeof callBack === 'function') {
          callBack()
        }
        dispatch(guardrails.actions.pending(false))
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export function deleteCategory(params: {
  id: string
  deleteContent?: boolean
}) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    return GuardrailsOpenAPI.deleteFolder(params)
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        toastSuccess('Category deleted.')
        dispatch(getGuardrailsCategories())
        dispatch(getGuardrailsList())
        dispatch(guardrails.actions.pending(false))
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export function updateCategory(
  id: string,
  folder: Folder,
  callback?: () => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    return GuardrailsOpenAPI.updateFolder({ id, folder })
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        toastSuccess('Category updated.')
        dispatch(getGuardrailsCategories())
        dispatch(getGuardrailsList())
        if (typeof callback === 'function') {
          callback()
        }
        dispatch(guardrails.actions.pending(false))
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export function addGuardrailFromCategory(
  guardrailId: string,
  folderId: string,
  callback?: () => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    return GuardrailsOpenAPI.addGuardrailIntoFolder({ guardrailId, folderId })
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        if (typeof callback === 'function') {
          callback()
        }
        dispatch(guardrails.actions.pending(false))
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}
export function deleteGuardrailFromCategory(
  guardrailId: string,
  folderId: string,
  callBack?: () => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    return GuardrailsOpenAPI.removeGuardrailFromFolder({
      guardrailId,
      folderId
    })
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        if (typeof callBack === 'function') {
          callBack()
        }
        dispatch(guardrails.actions.pending(false))
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

interface CreateCategoryParams extends GuardrailsOpenAPI.Folder {}

export function createCategory(options: CreateCategoryParams) {
  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    return GuardrailsOpenAPI.createFolder({ folder: { ...options } })
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        toastSuccess('Guardrail Category added.')
        dispatch(getGuardrailsCategories())
        dispatch(guardrails.actions.pending(false))
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export interface UpdateGuardrailOptions
  extends GuardrailsOpenAPI.UpdateGuardrailRequest {}

export function updateGuardrail(
  options: UpdateGuardrailOptions,
  callbackSuccess?: (
    res: GuardrailsOpenAPI.APIResponse<GuardrailsOpenAPI.Guardrail>
  ) => void
) {
  const userEmail = storage.get('userEmail')
  const { guardrail = {} } = options || ({} as UpdateGuardrailOptions)
  const { name } = guardrail

  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    return GuardrailsOpenAPI.updateGuardrail({
      ...options,
      guardrail: {
        ...options.guardrail,
        createdBy: correctEmailFormat(userEmail)
      }
    })
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        const message = 'Guardrail ' + name + ' updated.'
        if (callbackSuccess) {
          callbackSuccess(res)
        }
        toastSuccess(message)
        dispatch(guardrails.actions.updatedGuardrail(res))
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export interface CreateGuardrailOptions
  extends GuardrailsOpenAPI.CreateGuardrailRequest {}

export function createGuardrail(
  options: CreateGuardrailOptions,
  callbackSuccess: (
    res: GuardrailsOpenAPI.APIResponse<GuardrailsOpenAPI.Guardrail>
  ) => void
) {
  const { guardrail = {} } = options || ({} as CreateGuardrailOptions)
  const { name } = guardrail

  const userEmail = storage.get('userEmail')

  return (dispatch: any, getState: () => State) => {
    dispatch(guardrails.actions.pending(true))
    GuardrailsOpenAPI.createGuardrail({
      ...options,
      guardrail: {
        ...options.guardrail,
        createdBy: correctEmailFormat(userEmail)
      }
    })
      .then(res =>
        defaultHandleStatus(res, guardrails.actions.errors, true, dispatch)
      )
      .then(res => {
        const message = 'Guardrail ' + name + ' created.'
        if (callbackSuccess) {
          callbackSuccess(res)
        }
        toastSuccess(message)
        dispatch(guardrails.actions.createdGuardrail(res))
      })
      .catch((res: Response) =>
        dispatch(guardrails.actions.errors(res?.statusText))
      )
  }
}

export const { reducer, actions, caseReducers } = guardrails
export default guardrails
